/* eslint-disable */
// COPIA del motor de simulación del backend (backend/utils/worldSim.js) para correr la
// simulación en el navegador. Si cambia el del backend, re-copiar. Ver docs/ARQUITECTURA_CARRERA.md
// ============================================================================
//  Simulación DETERMINÍSTICA del "mundo" (ligas donde el jugador NO juega)
// ============================================================================
//
// Núcleo de la Etapa 3 de la arquitectura escalable. Hoy las 34 ligas se simulan
// con Math.random y su tabla final se GUARDA dentro de CareerSave (eso es el ~96%
// del documento y crece con cada liga nueva). La idea de fondo:
//
//   En vez de GUARDAR la tabla de cada liga ajena, se RECALCULA on-demand a partir
//   de una semilla determinística (saveSeed + temporada + ligaId). Mismo input →
//   misma tabla, siempre. Así el documento deja de crecer con el tamaño del mundo:
//   podés sumar 10 o 200 ligas y el save no engorda un byte.
//
// Este módulo es AUTÓNOMO y todavía NO está enchufado a los controllers (para no
// tocar el formato de las partidas en vivo sin validación). Es el motor que la
// Etapa 3 usará en lugar de `simulateLeagueToFinal` + tablas persistidas.
//
// Nota de diseño: la simulación de fondo NO necesita ser idéntica al motor del
// partido del jugador (matchEngine). Como en los juegos grandes, el mundo se
// resuelve con un modelo más barato (Poisson por diferencia de estrellas, sin
// jugador ni tácticas) — alcanza para tablas creíbles de ligas que el jugador
// solo mira de reojo.

// PRNG determinístico (mulberry32): rápido, 32-bit, sin dependencias. Mismo seed
// → misma secuencia de números en [0,1). Reemplaza a Math.random para que el mundo
// sea reproducible.
function makeRng(seed) {
    let a = seed >>> 0;
    return function () {
        a |= 0;
        a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// Combina varias partes (saveSeed, temporada, ligaId) en una semilla de 32 bits
// estable. Hash tipo FNV-1a sobre el string de las partes. Determinístico.
function hashSeed(...parts) {
    const str = parts.join('|');
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

// Muestra una Poisson con el PRNG inyectado (algoritmo de Knuth), igual que el
// matchEngine pero determinístico.
function poisson(lambda, rng) {
    const L = Math.exp(-lambda);
    let k = 0, p = 1;
    do { k++; p *= rng(); } while (p > L);
    return k - 1;
}

function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

// Simula UN partido de fondo (sin jugador ni tácticas) con el PRNG dado. Mismo
// modelo de jerarquía + forma del día que el matchEngine, para que las tablas del
// mundo tengan la misma "física" que la liga del jugador.
function simBackgroundMatch(home, away, rng) {
    const diff = (home.estrellas || 1) - (away.estrellas || 1);
    let lh = (1.4 + diff * 0.20 + 0.3) * (0.74 + rng() * 0.52);
    let la = (1.1 - diff * 0.20) * (0.74 + rng() * 0.52);
    lh = clamp(lh, 0.2, 4.5);
    la = clamp(la, 0.2, 4.5);
    return { gh: poisson(lh, rng), ga: poisson(la, rng) };
}

function sortStandings(clubs) {
    return [...clubs].sort((a, b) =>
        (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)) || (b.gf - a.gf) || String(a.tm_id).localeCompare(String(b.tm_id)));
}

// Genera el orden de un round-robin doble (ida y vuelta) de forma DETERMINÍSTICA a
// partir del PRNG (método del círculo, con la mezcla inicial barajada por el rng).
// No persiste nada: el fixture se deriva de la semilla cuando hace falta.
function deterministicFixtures(ids, rng) {
    const arr = [...ids];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    if (arr.length % 2 !== 0) arr.push(null);
    const n = arr.length, half = n / 2, rounds = n - 1;
    let rot = [...arr];
    const ida = [];
    for (let r = 0; r < rounds; r++) {
        const partidos = [];
        for (let i = 0; i < half; i++) {
            const a = rot[i], b = rot[n - 1 - i];
            if (a === null || b === null) continue;
            partidos.push(r % 2 === 0 ? [a, b] : [b, a]);
        }
        ida.push(partidos);
        const fixed = rot[0];
        const rest = rot.slice(1);
        rest.unshift(rest.pop());
        rot = [fixed, ...rest];
    }
    const vuelta = ida.map(ps => ps.map(([a, b]) => [b, a]));
    return [...ida, ...vuelta];
}

// Simula una temporada COMPLETA de una liga de fondo y devuelve la tabla final
// ordenada. DETERMINÍSTICA dado (clubs, seed): mismo input → misma tabla. Esta es
// la función que reemplaza a `simulateLeagueToFinal` + persistencia en la Etapa 3.
function simulateBackgroundSeason(clubs, seed) {
    const rng = makeRng(seed >>> 0);
    // Conservamos la identidad del club (logo/pais) que viene de la referencia: la simulación
    // determinística solo recalcula los números (pj/pts/...), no la identidad.
    const tabla = clubs.map(c => ({
        tm_id: c.tm_id, nombre: c.nombre, logo: c.logo || '', pais: c.pais || '', estrellas: c.estrellas,
        pj: 0, pg: 0, pe: 0, pp: 0, gf: 0, gc: 0, pts: 0,
    }));
    const byId = new Map(tabla.map(c => [c.tm_id, c]));
    for (const ronda of deterministicFixtures(tabla.map(c => c.tm_id), rng)) {
        for (const [hId, aId] of ronda) {
            const h = byId.get(hId), a = byId.get(aId);
            if (!h || !a) continue;
            const { gh, ga } = simBackgroundMatch(h, a, rng);
            h.pj++; a.pj++; h.gf += gh; h.gc += ga; a.gf += ga; a.gc += gh;
            if (gh > ga) { h.pg++; h.pts += 3; a.pp++; }
            else if (gh < ga) { a.pg++; a.pts += 3; h.pp++; }
            else { h.pe++; a.pe++; h.pts++; a.pts++; }
        }
    }
    return sortStandings(tabla);
}

// API pública pensada para los controllers (Etapa 3): tabla final de una liga del
// mundo, recalculada (no guardada) a partir de la semilla del save + temporada + id
// de liga. Cacheable por request.
function worldStandings(clubs, saveSeed, temporada, leagueId) {
    return simulateBackgroundSeason(clubs, hashSeed(saveSeed, temporada, leagueId));
}

// Simula deterministicamente solo las primeras `jornadasJugadas` fechas de una liga y
// devuelve { tabla, calendario }. Base para que la oferta de mitad de temporada muestre la
// MISMA posición que el jugador verá al incorporarse: ambas se derivan de esta simulación.
function worldLeaguePartial(clubs, seed, jornadasJugadas) {
    const rng = makeRng(seed >>> 0);
    const tabla = clubs.map(c => ({
        tm_id: c.tm_id, nombre: c.nombre, logo: c.logo || '', pais: c.pais || '', estrellas: c.estrellas,
        pj: 0, pg: 0, pe: 0, pp: 0, gf: 0, gc: 0, pts: 0,
    }));
    const byId = new Map(tabla.map(c => [c.tm_id, c]));
    const fixtures = deterministicFixtures(tabla.map(c => c.tm_id), rng);
    const calendario = [];
    fixtures.forEach((ronda, idx) => {
        const jugado = idx < jornadasJugadas;
        const partidos = [];
        for (const [hId, aId] of ronda) {
            if (jugado) {
                const h = byId.get(hId), a = byId.get(aId);
                const { gh, ga } = simBackgroundMatch(h, a, rng);
                h.pj++; a.pj++; h.gf += gh; h.gc += ga; a.gf += ga; a.gc += gh;
                if (gh > ga) { h.pg++; h.pts += 3; a.pp++; }
                else if (gh < ga) { a.pg++; a.pts += 3; h.pp++; }
                else { h.pe++; a.pe++; h.pts++; a.pts++; }
                partidos.push({ home_tm_id: hId, away_tm_id: aId, gh, ga, jugado: true });
            } else {
                partidos.push({ home_tm_id: hId, away_tm_id: aId, gh: null, ga: null, jugado: false });
            }
        }
        calendario.push({ jornada: idx + 1, partidos });
    });
    return { tabla: sortStandings(tabla), calendario };
}

// Igual que worldStandings pero para mitad de temporada: tabla + calendario tras
// `jornadasJugadas` fechas, deterministico para (saveSeed, temporada, ligaId).
function worldStandingsPartial(clubs, saveSeed, temporada, leagueId, jornadasJugadas) {
    return worldLeaguePartial(clubs, hashSeed(saveSeed, temporada, leagueId), jornadasJugadas);
}

export {    makeRng,
    hashSeed,
    deterministicFixtures,
    simulateBackgroundSeason,
    worldStandings,
    worldLeaguePartial,
    worldStandingsPartial,
};
